(() => {
  if (window.EmbryniumCart) return;

  const eventNames = Object.freeze({
    updated: 'embrynium:cart:updated',
    error: 'embrynium:cart:error',
  });

  let pendingRequest = null;

  const route = (name, fallback) => {
    if (window.routes?.[name]) return window.routes[name];
    const root = window.Shopify?.routes?.root || '/';
    return `${root}${fallback}`;
  };

  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));

  const parseJson = async (response) => {
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload.status) {
      throw new Error(payload.description || payload.message || 'The cart could not be updated.');
    }
    return payload;
  };

  const runLocked = async (operation) => {
    if (pendingRequest) throw new Error('A cart update is already in progress.');
    pendingRequest = Promise.resolve().then(operation);
    try {
      return await pendingRequest;
    } catch (error) {
      emit(eventNames.error, { error, message: error.message });
      throw error;
    } finally {
      pendingRequest = null;
    }
  };

  const getCart = async ({ sections = [] } = {}) => {
    const query = sections.length ? `?sections=${encodeURIComponent(sections.join(','))}` : '';
    const response = await fetch(`${route('cart_url', 'cart')}.js${query}`, {
      headers: { Accept: 'application/json' },
    });
    return parseJson(response);
  };

  const publishUpdate = async ({ cart, source, variantId = null }) => {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      await publish(PUB_SUB_EVENTS.cartUpdate, {
        source,
        productVariantId: variantId,
        cartData: cart,
      });
    }
    emit(eventNames.updated, { cart, source, variantId });
    return cart;
  };

  const surfaceSections = (surface) =>
    surface?.getSectionsToRender?.().map((section) => section.id).filter(Boolean) || [];

  const addForm = (formData, { activeElement = document.activeElement, source = 'embrynium' } = {}) =>
    runLocked(async () => {
      const surface = document.querySelector('cart-notification, cart-drawer');
      const sections = surfaceSections(surface);
      if (sections.length) {
        formData.set('sections', sections.join(','));
        formData.set('sections_url', window.location.pathname);
        surface.setActiveElement?.(activeElement);
      }

      const response = await fetch(route('cart_add_url', 'cart/add'), {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const addedLine = await parseJson(response);
      if (surface?.renderContents && addedLine.sections) surface.renderContents(addedLine);
      const cart = await getCart();
      return publishUpdate({ cart, source, variantId: String(formData.get('id')) });
    });

  const addItems = (items, { source = 'embrynium', notify = true } = {}) =>
    runLocked(async () => {
      const response = await fetch(`${route('cart_add_url', 'cart/add')}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });
      await parseJson(response);
      const cart = await getCart();
      return notify ? publishUpdate({ cart, source }) : cart;
    });

  const changeLine = ({ key, quantity, source = 'embrynium' }) =>
    runLocked(async () => {
      if (!key) throw new Error('A cart line key is required.');
      const response = await fetch(`${route('cart_change_url', 'cart/change')}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: Number(quantity) }),
      });
      const cart = await parseJson(response);
      return publishUpdate({ cart, source });
    });

  const updateLines = (updates, { source = 'embrynium', notify = true } = {}) =>
    runLocked(async () => {
      const response = await fetch(`${route('cart_update_url', 'cart/update')}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      const cart = await parseJson(response);
      return notify ? publishUpdate({ cart, source }) : cart;
    });

  window.EmbryniumCart = Object.freeze({
    eventNames,
    getCart,
    addForm,
    addItems,
    changeLine,
    updateLines,
    isBusy: () => Boolean(pendingRequest),
  });
})();
